// Admin sign-in (DC111) — the admin export's sign-in page (A §A.5, `… Admin
// Sign-in.dc.html`) restyled onto the console palette (decision 26 / DR-45: not the
// page's own fourth palette or Inter). Phone + OTP only (DR-34): the export's password,
// new-device, set-password and forgot steps, its demo-numbers and dev-note cards, and
// its pre-verify "locked" / "no access" answers are not carried — the request uses
// `admin_login`, which is a generic 202 for every number (a code is only sent to an
// active admin), so the page never learns whether a number is an admin (§4.1).
//
// Flow: phone → POST /auth/otp/request (admin_login) → six-box code → POST
// /auth/otp/verify (client web: the HttpOnly refresh cookie is set server-side, the
// access token is held in memory via setAccessToken, AP-D4) → welcome card with the
// admin's name and role, which continues into the console (onSuccess reloads the
// session). The only "no access" message is the gate's, after a non-admin session
// already exists (auth-gate.tsx passes `denied`).
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { api, setAccessToken, signOut, ApiError } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { useTheme, type ThemePref } from '../../shell/theme-context';
import { isValidMobile, maskPhone, toE164 } from '@bookingdei/i18n/phone';
import { PhoneField } from '../../shell/phone-field';
import { brand } from '../../theme/tokens';
import { phonePlan } from '../../theme/phone-plan';
import { brandAssets } from '../../theme/brand-assets';
import './login.css';

const CODE_LENGTH = 6;
// Server truths: 5 tries per code (INF-8). Resend pacing is the export's (30 s, 3x);
// the 60 s cooldown follows a 429 or `otp_attempts_exceeded` (retryAfter 60).
const MAX_TRIES = 5;
const RESEND_WAIT_S = 30;
const RESEND_MAX = 3;
const COOLDOWN_S = 60;
const CONTINUE_MS = 1500;

type Step = 'phone' | 'otp' | 'done';
type OtpNote = 'wrong' | 'resent' | 'failed' | null;

interface SignedInUser {
  name?: string;
  grants?: string[];
}


const THEME_OPTS: { pref: ThemePref; icon: string; key: MessageKey }[] = [
  { pref: 'light', icon: '☀', key: 'admin.signin.theme_light' },
  { pref: 'dark', icon: '☽', key: 'admin.signin.theme_dark' },
];

export function LoginScreen({ onSuccess, denied = false }: { onSuccess: () => void; denied?: boolean }) {
  const { t, lang } = useLanguage();
  const { pref, setPref } = useTheme();
  const [step, setStep] = useState<Step>('phone');
  const [digits, setDigits] = useState('');
  const [phoneErr, setPhoneErr] = useState<'bad' | 'noaccess' | 'failed' | null>(denied ? 'noaccess' : null);
  const [code, setCode] = useState('');
  const [otpNote, setOtpNote] = useState<OtpNote>(null);
  const [triesLeft, setTriesLeft] = useState(MAX_TRIES);
  const [shake, setShake] = useState(0);
  const [cool, setCool] = useState(0);
  const [resendIn, setResendIn] = useState(0);
  const [resends, setResends] = useState(0);
  const [busy, setBusy] = useState(false);
  const [who, setWho] = useState<SignedInUser | null>(null);
  const codeInput = useRef<HTMLInputElement>(null);
  const leaveTimer = useRef<number | undefined>(undefined);
  const fmt = (key: MessageKey, params: Record<string, string | number> = {}) => formatMessage(lang, key, params);

  useEffect(() => {
    if (denied) setPhoneErr('noaccess');
  }, [denied]);

  const ticking = cool > 0 || resendIn > 0;
  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => {
      setCool((n) => (n > 0 ? n - 1 : 0));
      setResendIn((n) => (n > 0 ? n - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [ticking]);
  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);
  useEffect(() => {
    if (step === 'otp' && !busy) codeInput.current?.focus();
  }, [step, busy]);

  const e164 = toE164(digits, phonePlan) ?? '';
  const masked = maskPhone(e164, phonePlan);

  const send = async (): Promise<'ok' | 'rate' | 'failed'> => {
    try {
      // Always a generic 202 — never reveals whether the number is an admin (§4.1).
      await api.POST('/auth/otp/request', { body: { phone: e164, purpose: 'admin_login' } });
      return 'ok';
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setCool(COOLDOWN_S);
        return 'rate';
      }
      return 'failed';
    }
  };

  const submitPhone = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || cool > 0) return;
    if (!isValidMobile(digits, phonePlan)) {
      setPhoneErr('bad');
      return;
    }
    setBusy(true);
    setPhoneErr(null);
    const sent = await send();
    setBusy(false);
    if (sent !== 'ok') {
      if (sent === 'failed') setPhoneErr('failed');
      return;
    }
    setCode('');
    setOtpNote(null);
    setTriesLeft(MAX_TRIES);
    setResends(0);
    setResendIn(RESEND_WAIT_S);
    setStep('otp');
  };

  const verify = async (full: string) => {
    setBusy(true);
    setOtpNote(null);
    try {
      const res = (await api.POST('/auth/otp/verify', {
        body: { phone: e164, code: full, purpose: 'admin_login', client: 'web' },
      })) as { access_token?: string; user?: SignedInUser };
      setAccessToken(res.access_token ?? null);
      setWho(res.user ?? {});
      setBusy(false);
      setStep('done');
      leaveTimer.current = window.setTimeout(onSuccess, CONTINUE_MS);
    } catch (err) {
      setBusy(false);
      setCode('');
      setShake((n) => n + 1);
      if (err instanceof ApiError && err.code === 'otp_attempts_exceeded') {
        setTriesLeft(MAX_TRIES);
        setCool(COOLDOWN_S);
      } else if (err instanceof ApiError && err.code === 'otp_invalid') {
        const left = triesLeft - 1;
        if (left <= 0) {
          setTriesLeft(MAX_TRIES);
          setCool(COOLDOWN_S);
        } else {
          setTriesLeft(left);
          setOtpNote('wrong');
        }
      } else {
        setOtpNote('failed');
      }
    }
  };

  const onCode = (raw: string) => {
    const next = raw.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(next);
    if (next) setOtpNote(null);
    if (next.length === CODE_LENGTH && !busy && cool === 0) void verify(next);
  };

  const resend = async () => {
    if (busy || resendIn > 0 || resends >= RESEND_MAX || cool > 0) return;
    setBusy(true);
    const sent = await send();
    setBusy(false);
    if (sent !== 'ok') {
      if (sent === 'failed') setOtpNote('failed');
      return;
    }
    setResends((n) => n + 1);
    setResendIn(RESEND_WAIT_S);
    setTriesLeft(MAX_TRIES);
    setCode('');
    setOtpNote('resent');
  };

  const restart = () => {
    setStep('phone');
    setCode('');
    setOtpNote(null);
    setPhoneErr(null);
  };

  const leave = async () => {
    window.clearTimeout(leaveTimer.current);
    await signOut();
    setWho(null);
    setDigits('');
    restart();
  };

  const coolLine = cool > 0 ? fmt('admin.signin.cool', { n: cool }) : null;
  const phoneErrLine =
    coolLine ??
    (phoneErr === 'bad'
      ? fmt('phone.invalid', { n: phonePlan.nationalDigits, dial: phonePlan.dial, lead: phonePlan.mobileLead })
      : phoneErr === 'noaccess'
        ? t('admin.signin.no_access')
        : phoneErr === 'failed'
          ? t('admin.signin.send_failed')
          : null);
  const otpLine =
    coolLine ??
    (otpNote === 'wrong'
      ? fmt('admin.signin.wrong', { n: triesLeft })
      : otpNote === 'resent'
        ? fmt('admin.signin.resent', { phone: masked })
        : otpNote === 'failed'
          ? t('admin.signin.verify_failed')
          : null);
  const resendLabel =
    resendIn > 0
      ? fmt('admin.signin.resend_in', { n: resendIn })
      : resends >= RESEND_MAX
        ? t('admin.signin.resend_max')
        : t('admin.signin.resend');
  const resendOff = busy || resendIn > 0 || resends >= RESEND_MAX || cool > 0;
  const modeWord = t(`admin.signin.mode_${pref}` as MessageKey);
  const themeNote = t('admin.signin.theme_note_default');
  const grants = who?.grants ?? [];
  const roleLabel = grants.length ? t(`admin.grant.${grants[0]}` as MessageKey) : null;

  return (
    <div className="asi">
      <div className="asi-top">
        <div className="asi-theme" role="group" aria-label={t('admin.signin.theme')}>
          {THEME_OPTS.map((o) => (
            <button
              key={o.pref}
              type="button"
              className={`asi-theme__opt asi-theme__opt--${o.pref}`}
              aria-pressed={pref === o.pref}
              aria-label={t(o.key)}
              title={t(o.key)}
              onClick={() => setPref(o.pref)}
            >
              <span aria-hidden="true">{o.icon}</span>
            </button>
          ))}
        </div>
        <div className="asi-theme__note">{themeNote}</div>
      </div>

      <main className="asi-card">
        <div className="asi-head">
          <img className="asi-logo" src={brandAssets.lockup} alt={brand.name} />
          <img className="asi-logo asi-logo--rev" src={brandAssets.lockupReversed} alt="" aria-hidden="true" />
          <span className="asi-tag">{t('admin.signin.console_tag')}</span>
        </div>

        {step === 'phone' && (
          <form className="asi-step" onSubmit={submitPhone} noValidate>
            <h1 className="asi-h1">{t('admin.signin.title')}</h1>
            <p className="asi-sub">{t('admin.signin.sub')}</p>
            <label className="asi-label" htmlFor="asi-phone">
              {t('admin.signin.phone_label')}
            </label>
            <PhoneField
              id="asi-phone"
              className="asi-phone"
              value={digits}
              invalid={phoneErr === 'bad'}
              describedBy={phoneErrLine ? 'asi-phone-err' : undefined}
              onChange={(d) => {
                setDigits(d);
                setPhoneErr(null);
              }}
            />
            {phoneErrLine && (
              <div id="asi-phone-err" className="asi-err" role="alert">
                {phoneErrLine}
              </div>
            )}
            <button className="asi-primary" type="submit" data-off={cool > 0 || undefined} aria-disabled={busy || cool > 0}>
              {cool > 0 ? fmt('admin.signin.wait', { n: cool }) : t('admin.signin.continue')}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <div className="asi-step">
            <h1 className="asi-h1">{t('admin.signin.otp_title')}</h1>
            <p className="asi-sub">{fmt('admin.signin.otp_sub', { phone: masked })}</p>
            <div
              className="asi-otp"
              data-shake={shake % 2 === 1 ? 'a' : shake > 0 ? 'b' : undefined}
              data-error={otpNote === 'wrong' || cool > 0 || undefined}
            >
              <div className="asi-otp__boxes" aria-hidden="true">
                {Array.from({ length: CODE_LENGTH }, (_, i) => (
                  <span key={i} className={`asi-otp__box${code[i] ? ' is-filled' : ''}${i === Math.min(code.length, CODE_LENGTH - 1) ? ' is-active' : ''}`}>
                    {code[i] ?? ''}
                  </span>
                ))}
              </div>
              <input
                ref={codeInput}
                className="asi-otp__input"
                value={code}
                onChange={(e) => onCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={CODE_LENGTH}
                aria-label={t('admin.signin.code_label')}
                disabled={busy || cool > 0}
              />
            </div>
            {otpLine && (
              <div className={otpNote === 'resent' && !coolLine ? 'asi-ok' : 'asi-err'} role={otpNote === 'resent' ? 'status' : 'alert'}>
                {otpLine}
              </div>
            )}
            <div className="asi-row">
              <button type="button" className="asi-linkbtn" onClick={restart}>
                {t('admin.signin.back')}
              </button>
              <button type="button" className="asi-resend" data-off={resendOff || undefined} aria-disabled={resendOff} onClick={() => void resend()}>
                {resendLabel}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="asi-done" role="status">
            <div className="asi-done__check" aria-hidden="true">
              <svg viewBox="0 0 44 44" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 22.6l6 6L31.5 16" />
              </svg>
            </div>
            <h1 className="asi-done__title">
              {who?.name ? fmt('admin.signin.welcome', { name: who.name }) : t('admin.signin.welcome_anon')}
            </h1>
            {roleLabel && (
              <div className={`asi-role${grants.includes('super_admin') ? ' asi-role--super' : ''}`}>{roleLabel}</div>
            )}
            <p className="asi-done__sub">{t('admin.signin.done_sub')}</p>
            <div className="asi-redirect">
              <span className="asi-dot" />
              <span className="asi-dot" />
              <span className="asi-dot" />
              <span className="asi-redirect__text">{fmt('admin.signin.ready', { mode: modeWord })}</span>
            </div>
            <button
              type="button"
              className="asi-primary"
              onClick={() => {
                window.clearTimeout(leaveTimer.current);
                onSuccess();
              }}
            >
              {t('admin.signin.open')}
            </button>
            <button type="button" className="asi-ghost" onClick={() => void leave()}>
              {t('admin.signin.sign_out')}
            </button>
          </div>
        )}

        <p className="asi-foot">{t('admin.signin.foot')}</p>
      </main>
    </div>
  );
}
